import React, { PureComponent, Fragment, createRef } from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import Link from 'next/link';
import {
  CardNumberElement,
  CardExpiryElement,
  CardCVCElement,
  PostalCodeElement,
} from 'react-stripe-elements';
import { Formik, Form } from 'formik';
import { CardSelect } from '../index';
import { Button, Checkbox } from '../../components';

import {
  InvalidCross,
  getCardBrandIcon,
  axiosInstance,
} from '../../utils';

import {
  CardInputContainer,
  CardInputRow,
  ClientInfoHeading,
  ClientInfoList,
  ClientInfoNote,
  InfoInputErrorTooltip,
  InfoInputContainer,
  StripeInput,
  CardIcon,
  InfoInput,
  GuestLoginContainer,
  GuestLoginHeading,
  GuestLoginButtonContainer,
  PaymentProcessError,
  baseInputStyle,
  OptionsSubHeading,
} from '../PaymentForm/styles';

import {
  PanelPaymentButtonContainer,
  PanelPaymentCheckboxContainer,
  CheckboxDescription,
} from '../PassPanel/styles';

class PaymentWizardPass extends PureComponent {
  static propTypes = {
    setFormikInstance: PropTypes.func,
    setUpFieldInstances: PropTypes.func,
    handleFormSubmit: PropTypes.func,
    handleLoginButtonClick: PropTypes.func,
    handleCardElementsInput: PropTypes.func,
    baseInitialValues: PropTypes.instanceOf(Object),
    baseScheme: PropTypes.instanceOf(Object),
    cardErrors: PropTypes.instanceOf(Object),
    userData: PropTypes.instanceOf(Object),
    status: PropTypes.string,
    brand: PropTypes.string,
    payingWith: PropTypes.instanceOf(Object),
    error: PropTypes.string,
    planName: PropTypes.string,
  };

  state = {
    isChecked: false,
    isDisabled: true,
  };

  form = createRef();

  cardNumber = createRef();

  cardExpiry = createRef();

  cardCvc = createRef();

  postalCode = createRef();

  componentDidMount() {
    const { setFormikInstance, setUpFieldInstances } = this.props;

    setUpFieldInstances(this.cardNumber.current, this.cardExpiry.current, this.cardCvc.current, this.postalCode.current);

    setFormikInstance(this.form.current);
  }

  componentDidUpdate(prevProps) {
    const { userData: oldData, payingWith: oldPaymentMethod } = prevProps;
    const { userData: newData, payingWith: newPaymentMethod, setUpFieldInstances } = this.props;

    if (JSON.stringify(oldData) !== JSON.stringify(newData) || JSON.stringify(oldPaymentMethod)!== JSON.stringify(newPaymentMethod)) {
      setUpFieldInstances(this.cardNumber.current, this.cardExpiry.current, this.cardCvc.current, this.postalCode.current);
    }
  }

  onEmailBlur = async (evt, formikHandleBlurFunc) => {
    formikHandleBlurFunc(evt);

    if (!this.form.current.state.errors.emailAddress) {
      try {
        const { target: { value } } = evt;
        await axiosInstance.post('users/validate_email', { email: value });
      } catch (error) {
        this.form.current.setFieldError('emailAddress', error)
      }
    }
  };

  onFormSubmit = values => {
    const { handleFormSubmit, planName } = this.props;

    handleFormSubmit({ ...values, planName }, 'pass');
  };

  render() {
    const { onFormSubmit, onEmailBlur } = this;
    const { cardErrors, brand, handleLoginButtonClick, userData, status, handleCardElementsInput, baseScheme, baseInitialValues, payingWith, error } = this.props;
    const { cardNumber, cardExpiry, cardCvc, postalCode } = cardErrors;
    const { isChecked, isDisabled } = this.state;

    const WizardScheme = Yup.object().shape({
      ...baseScheme,
    });

    return (
      <article>
        <GuestLoginContainer>
          <GuestLoginHeading>Guest check-in information</GuestLoginHeading>
          {!userData && (
            <GuestLoginButtonContainer>
              <Button htmlType="button" theme="secondary" onClick={() => handleLoginButtonClick('login')}>Login</Button>
            </GuestLoginButtonContainer>
          )}
          {status === 'hasAccount' && <PaymentProcessError>Looks like you already have an account. Please, log in</PaymentProcessError>}
          {status === 'failure' && <PaymentProcessError>{error || <Fragment>There is a problem with your purchase. Our engineers have been notified. Please try again later or contact our customer service for more details.</Fragment>}</PaymentProcessError>}
        </GuestLoginContainer>
        <Formik
          enableReinitialize
          initialValues={baseInitialValues}
          validationSchema={payingWith && (payingWith.value === 'newCard' || payingWith.value === 'paypal') ? WizardScheme : null}
          onSubmit={onFormSubmit}
          ref={this.form}
        >
          {({ errors, touched, handleBlur, handleChange, values }) => (
            <Form>
              <OptionsSubHeading>Pay with:</OptionsSubHeading>
              <CardSelect cardOnly />
                <ClientInfoList>
                  {payingWith && (payingWith.value === 'newCard' || payingWith.value === 'paypal') ? (
                    <Fragment>
                      <li>
                        <ClientInfoHeading htmlFor="firstName">First name</ClientInfoHeading>
                        <InfoInputContainer hasError={errors.firstName && touched.firstName}>
                          <InfoInput
                            mask={false}
                            value={values.firstName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="firstName"
                            id="firstName"
                            placeholder="Enter first name"
                            type="text"
                          />
                          {errors.firstName && touched.firstName && (
                            <InfoInputErrorTooltip
                              content={errors.firstName}
                              placement="left"
                              fadeDuration={100}
                              padding={20}
                              radius={6}
                              background="#ffffff"
                              color="#5a5a5a"
                              border="#ebebeb"
                            >
                              <InvalidCross color="#ff4e72" />
                            </InfoInputErrorTooltip>
                          )}
                        </InfoInputContainer>
                      </li>
                      <li>
                        <ClientInfoHeading htmlFor="lastName">Last name</ClientInfoHeading>
                        <InfoInputContainer hasError={errors.lastName && touched.lastName}>
                          <InfoInput
                            mask={false}
                            value={values.lastName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="lastName"
                            id="lastName"
                            placeholder="Enter last name"
                            type="text"
                          />
                          {errors.lastName && touched.lastName && (
                            <InfoInputErrorTooltip
                              content={errors.lastName}
                              placement="left"
                              fadeDuration={100}
                              padding={20}
                              radius={6}
                              background="#ffffff"
                              color="#5a5a5a"
                              border="#ebebeb"
                            >
                              <InvalidCross color="#ff4e72" />
                            </InfoInputErrorTooltip>
                          )}
                        </InfoInputContainer>
                      </li>
                      <li>
                        <ClientInfoHeading htmlFor="card">Card</ClientInfoHeading>
                        <CardInputContainer>
                          <CardInputRow>
                            <InfoInputContainer>
                              <StripeInput
                                style={{ borderTopRightRadius: 6, borderTopLeftRadius: 6 }}
                              >
                                <CardNumberElement style={{ ...baseInputStyle }} onChange={handleCardElementsInput} ref={this.cardNumber} />
                              </StripeInput>
                              <CardIcon>
                                {getCardBrandIcon(brand)}
                              </CardIcon>
                              {cardNumber && (
                                <InfoInputErrorTooltip
                                  content={cardNumber}
                                  style={{ right: getCardBrandIcon(brand) ? 70 : 20 }}
                                  placement="left"
                                  fadeDuration={100}
                                  padding={20}
                                  radius={6}
                                  background="#ffffff"
                                  color="#5a5a5a"
                                  border="#ebebeb"
                                >
                                  <InvalidCross color="#ff4e72" />
                                </InfoInputErrorTooltip>
                              )}
                            </InfoInputContainer>
                          </CardInputRow>
                          <CardInputRow>
                            <InfoInputContainer hasError={errors.cardExpiration && touched.cardExpiration}>
                              <StripeInput>
                                <CardExpiryElement style={{ ...baseInputStyle }} onChange={handleCardElementsInput} ref={this.cardExpiry} />
                              </StripeInput>
                              {cardExpiry && (
                                <InfoInputErrorTooltip
                                  content={cardExpiry}
                                  placement="left"
                                  fadeDuration={100}
                                  padding={20}
                                  radius={6}
                                  background="#ffffff"
                                  color="#5a5a5a"
                                  border="#ebebeb"
                                >
                                  <InvalidCross color="#ff4e72" />
                                </InfoInputErrorTooltip>
                              )}
                            </InfoInputContainer>
                            <InfoInputContainer hasError={errors.cvc && touched.cvc}>
                              <StripeInput>
                                <CardCVCElement style={{ ...baseInputStyle }} onChange={handleCardElementsInput} ref={this.cardCvc} />
                              </StripeInput>
                              {cardCvc && (
                                <InfoInputErrorTooltip
                                  content={cardCvc}
                                  placement="left"
                                  fadeDuration={100}
                                  padding={20}
                                  radius={6}
                                  background="#ffffff"
                                  color="#5a5a5a"
                                  border="#ebebeb"
                                >
                                  <InvalidCross color="#ff4e72" />
                                </InfoInputErrorTooltip>
                              )}
                            </InfoInputContainer>
                          </CardInputRow>
                          <CardInputRow>
                            <InfoInputContainer hasError={errors.zipCode && touched.zipCode}>
                              <StripeInput
                                style={{ borderBottomLeftRadius: 6, borderBottomRightRadius: 6 }}
                              >
                                <PostalCodeElement style={{ ...baseInputStyle }} onChange={handleCardElementsInput} ref={this.postalCode} />
                              </StripeInput>
                              {postalCode && (
                                <InfoInputErrorTooltip
                                  content={postalCode}
                                  placement="left"
                                  fadeDuration={100}
                                  padding={20}
                                  radius={6}
                                  background="#ffffff"
                                  color="#5a5a5a"
                                  border="#ebebeb"
                                >
                                  <InvalidCross color="#ff4e72" />
                                </InfoInputErrorTooltip>
                              )}
                            </InfoInputContainer>
                          </CardInputRow>
                        </CardInputContainer>
                      </li>
                      <li style={{display: userData ? 'none' : 'list-item'}}>
                        <ClientInfoHeading htmlFor="emailAddress">E-mail</ClientInfoHeading>
                        <InfoInputContainer hasError={errors.emailAddress && touched.emailAddress}>
                          <InfoInput
                            mask={false}
                            value={values.emailAddress}
                            onChange={handleChange}
                            onBlur={evt => onEmailBlur(evt, handleBlur)}
                            name="emailAddress"
                            id="emailAddress"
                            placeholder="Enter e-mail"
                            type="email"
                          />
                          {errors.emailAddress && touched.emailAddress && (
                            <InfoInputErrorTooltip
                              content={errors.emailAddress}
                              placement="left"
                              fadeDuration={100}
                              padding={20}
                              radius={6}
                              background="#ffffff"
                              color="#5a5a5a"
                              border="#ebebeb"
                            >
                              <InvalidCross color="#ff4e72" />
                            </InfoInputErrorTooltip>
                          )}
                        </InfoInputContainer>
                        <ClientInfoNote withError={errors.emailAddress === 'User with this email already exists'}>{errors.emailAddress === 'User with this email already exists' ? errors.emailAddress : 'A booking confirmation will be sent to this address'}</ClientInfoNote>
                      </li>
                      <li>
                        <ClientInfoHeading htmlFor="phone">Phone</ClientInfoHeading>
                        <InfoInputContainer hasError={errors.phone && touched.phone}>
                          <InfoInput
                            mask={['+', '1', ' ', '(', /[1-9]/, /\d/, /\d/, ')', ' ', /\d/, /\d/, /\d/, '-', /\d/, /\d/, /\d/, /\d/]}
                            guide={false}
                            value={values.phone}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            name="phone"
                            id="phone"
                            placeholder="+1 (800) XXX-XXXX"
                            type="tel"
                          />
                          {errors.phone && touched.phone && (
                            <InfoInputErrorTooltip
                              content={errors.phone}
                              placement="left"
                              fadeDuration={100}
                              padding={20}
                              radius={6}
                              background="#ffffff"
                              color="#5a5a5a"
                              border="#ebebeb"
                            >
                              <InvalidCross color="#ff4e72" />
                            </InfoInputErrorTooltip>
                          )}
                        </InfoInputContainer>
                      </li>
                    </Fragment>
                  ) : null}
                  <li>
                    <PanelPaymentCheckboxContainer>
                      <Checkbox
                        name="terms"
                        value="terms"
                        id="terms"
                        blue
                        invalid={isChecked || !isDisabled}
                        checked={!isDisabled}
                        onChange={() => { this.setState((prevState) => ({ isDisabled: !prevState.isDisabled }) )}}
                        label={(
                          <CheckboxDescription>
                            By purchasing, I understand that my membership
                            automatically renews at the standard monthly
                            subscription rate and I can cancel on site at
                            any time. I agree to the{' '}
                            <Link href="/terms">
                              <a target="_blank" rel="noopener noreferrer">Terms and Conditions</a>
                            </Link>
                          </CheckboxDescription>
                        )}
                      />
                    </PanelPaymentCheckboxContainer>
                    <PanelPaymentButtonContainer>
                      <Button
                        styles={{width: '48% !important'}}
                        htmlType="button"
                        theme="primary"
                        onClick={isDisabled && (() => {this.setState({isChecked:true})}) ||  this.form.current && this.form.current.submitForm}
                      >
                        Purchase
                      </Button>
                    </PanelPaymentButtonContainer>
                  </li>
                </ClientInfoList>
            </Form>
          )}
        </Formik>
      </article>
    );
  }
}

export default PaymentWizardPass;
